import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { getConversationOptions } from '../../generated/api/@tanstack/react-query.gen.js';

interface ConversationDetailLive {
  /** The stream is waiting for the agent: the row (status, summary) changes over that wait, so re-read it. */
  awaitingAssistant: boolean;
  /** The newest message of the stream; a new assistant message means the row changed. */
  lastMessage: { id: string; role: string } | null;
}

/**
 * The conversation's own row (topic, status, summary, runtime policy), kept fresh while the agent works. Its key is
 * the generated `getConversationQueryKey`, the one the chat transport and the interaction cards invalidate. A caller
 * must render `isError` (the row failed to load) apart from a missing row.
 */
export function useConversationDetail(conversationId: string | null, live: ConversationDetailLive) {
  const query = useQuery({
    ...getConversationOptions({ path: { id: conversationId ?? '' } }),
    enabled: Boolean(conversationId),
    refetchInterval: live.awaitingAssistant ? 2_000 : false,
  });

  const { refetch } = query;
  const newestAssistantId = live.lastMessage?.role === 'assistant' ? live.lastMessage.id : null;
  useEffect(() => {
    if (newestAssistantId) void refetch();
  }, [newestAssistantId, refetch]);

  // The failure is part of the result on purpose: both callers render it (an error card with Retry), never an empty
  // conversation.
  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch,
  };
}
